import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'app/store/rootReducer'; 
import { ProposalsList } from './proposalsList';
import Skeleton from 'react-loading-skeleton';
import { AppSearchForm } from 'components/SearchForm';
import { SubmitProposalModal } from 'features/proposals/proposalModals/SubmitProposalModal';
import { ProposalsStatsWidget } from './ProposalsStatsWidget';
import useModal from 'helpers/useModal';
import useUserInput from 'helpers/useUserInput';
import useSearchable from 'helpers/useSearchable';
import { fetchProposals } from './proposalsListSlice';
import { getUserProposalVotes } from "services/user-service";
import { useAuth } from '../../../helpers/authContext';
import { useLocation, useNavigate } from 'react-router-dom';
import VotingModal from 'features/proposals/proposalModals/VotingModal';

export const ProposalsListPage = () => {
  const dispatch = useDispatch();
  const searchText = useUserInput("");
  const { isOpenNewProposalModal, toggleNewProposalModal, isOpenVotingModal, toggleVotingModal } = useModal();
  const { user, updateProposals } = useAuth(); 
  const location = useLocation();
  const navigate = useNavigate();
  const [urlProposalId, setUrlProposalId] = React.useState<number | string>("");

  const {
    passingProposals,
    nonPassingProposals,
    returnProposal,
    totalProposals,
    totalBudget,
    dailyBudget, 
    isLoading
  } = useSelector((state: RootState) => state.proposals);
  
  const reloadProposals = (silent: boolean = false): Promise<void> => {
    return new Promise(async (resolve) => {
      const promises: Promise<any>[] = [];
      if (user) {
        promises.push(getUserProposalVotes(user).then(result => { 
          updateProposals(result.voterProposals);
        }));
      }
      promises.push(dispatch(fetchProposals(silent) as any));
      await Promise.all(promises);
      resolve();
    });
  };

  useEffect(() => { 
      reloadProposals(); 
  }, [dispatch]); 

  useEffect(() => {
    if (user) {
      getUserProposalVotes(user).then(result => { 
        updateProposals(result.voterProposals);
      });
    }
  }, [user]); 

  useEffect(() => {
      if (!user) return; // Wait until logged in
      const paramsStr = location.search;
      if (!paramsStr) return;

      const searchParams = new URLSearchParams(paramsStr);
      const op = searchParams.get('op');
      if (op === 'voteProposal') {
          const id = searchParams.get('id');
          if (id) {
              setUrlProposalId(id);
              if (!isOpenVotingModal) toggleVotingModal();
          }

          // Remove URL params to avoid re-triggering on refresh
          navigate(location.pathname, { replace: true });
      }
  }, [location.search, user]);

  const searchablePassingProposals = useSearchable(
    passingProposals,
    searchText.value,
    (l: any) => [l.subject, l.creator]
  );

  const searchableNonPassingProposals = useSearchable(
    nonPassingProposals,
    searchText.value,
    (l: any) => [l.subject, l.creator]
  ); 



  const renderSearchWidget = (
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center mb-4 gap-2 gap-md-3">
      <div className="mb-3 mb-md-0 flex-grow-1" style={{ maxWidth: '100%', width: '100%' }}>
        <div style={{ maxWidth: '1000px' }} className="w-100 container-search-limit">
          <AppSearchForm {...searchText} />
        </div>
      </div>
      <button 
        className="btn btn-speem d-flex align-items-center justify-content-center gap-2 px-4 shadow-sm" 
        onClick={toggleNewProposalModal}
        style={{ height: '45px', borderRadius: '10px', minWidth: '180px' }}
      >
        <i className="fas fa-plus-circle" />
        <span>Submit Proposal</span>
      </button>
    </div>
  );

  const renderNewProposalModal = (
    <SubmitProposalModal
      isShowing={isOpenNewProposalModal}
      hide={toggleNewProposalModal} />
  ); 

  const renderStatsWidget = (
    <ProposalsStatsWidget
      totalProposals={totalProposals}
      totalBudget={totalBudget}
      dailyBudget={dailyBudget}
    />
  );

  const renderList = isLoading ? (
    <Skeleton count={5} height={30} duration={3} />
  ) : (
      <div>
        <ProposalsList
          passingProposals={searchablePassingProposals}
          nonPassingProposals={searchableNonPassingProposals}
          returnProposal={returnProposal}
          reloadProposals={reloadProposals} 
        />
      </div>
    );

  return (
    <div className="p-3">
      <div className="mb-4">
        <h1 className="font-weight-bold mb-1">Proposals</h1>
        <p className="text-secondary3">Support the development of the Steem ecosystem.</p>
      </div>
      {renderStatsWidget}
      <hr className="my-4 opacity-5" />
      {renderSearchWidget}
      {renderList}
      {renderNewProposalModal}
      <VotingModal
        isShowing={isOpenVotingModal}
        hide={toggleVotingModal}
        proposalId={urlProposalId}
        onSuccess={() => reloadProposals(true)}
      />
    </div>
  );
}
